import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  ViewEncapsulation,
  OnDestroy,
  inject,
  output,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NotificationComponent,
  FaIconComponent,
  Severity,
  ContextMenuItem,
  ContextMenuComponent,
  ButtonComponent,
} from '@akostm/orbit-ui';
import { TranslateModule } from '@ngx-translate/core';
import {
  faEllipsis,
  faEllipsisVertical,
  faLink,
  faPencil,
} from '@fortawesome/free-solid-svg-icons';
import { MessageStore, ParticipantWithSourceId, UserState } from '../../../stores';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ParticipantKind, UserRole } from '@clientwire/api';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[shared-participant-list-item]',
  standalone: true,
  imports: [CommonModule, NotificationComponent, FaIconComponent, TranslateModule, ButtonComponent],
  templateUrl: './participant-list-item.component.html',
  styleUrl: './participant-list-item.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantListItemComponent implements OnDestroy {
  user = input<UserState>();
  participant = input.required<ParticipantWithSourceId>();
  menuClicked = output<{ type: string; participant?: Signal<ParticipantWithSourceId> }>();
  #now = signal<Date>(new Date());
  conversationIsOpen = computed(
    () => this.#now().getTime() - (this.participant().hadConversationOpenAt?.getTime() ?? 0) < 10000
  );
  messages = computed(() => this.#messageStore.entities()[0]?.messages ?? []);

  isTyping = computed(() => {
    if (this.participant().sourceId) {
      const lastMessage = this.messages()[0];
      return (
        this.#now().getTime() - (this.participant().wasTypingAt?.getTime() ?? 0) < 5000 &&
        lastMessage?.sourceId !== this.participant().sourceId
      );
    }
    return this.#now().getTime() - (this.participant().wasTypingAt?.getTime() ?? 0) < 5000;
  });

  #dateUpdaterTimeout: number | null = null;
  readonly SeverityEnum = Severity;
  readonly typingIcon = faEllipsis;
  readonly menuIcon = faEllipsisVertical;
  // TODO: try to remove the message store and add the needed value as an input
  readonly #messageStore = inject(MessageStore);
  readonly #overlay = inject(Overlay);
  readonly editIcon = faPencil;
  readonly linkIcon = faLink;
  readonly UserRole = UserRole;
  readonly ParticipantKind = ParticipantKind;
  readonly FULL_MENU: ContextMenuItem[] = [
    {
      label: 'SHARED.PARTICIPANT_LIST.MENU.EDIT',
      icon: this.editIcon,
      clickValue: {
        type: 'edit',
        participant: this.participant,
      },
    },
    {
      label: 'SHARED.PARTICIPANT_LIST.MENU.LINK',
      icon: this.linkIcon,
      clickValue: {
        type: 'link',
        participant: this.participant,
      },
    },
  ];

  constructor() {
    this.#updateDateSignalToCheck();
  }

  ngOnDestroy(): void {
    if (this.#dateUpdaterTimeout !== null) {
      clearTimeout(this.#dateUpdaterTimeout);
    }
  }

  openMenu(event: Event) {
    const positionStrategy = this.#overlay
      .position()
      .flexibleConnectedTo(event.target as HTMLElement)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetX: 15,
        } as ConnectedPosition,
      ])
      .withPush(false);
    const scrollStrategy = this.#overlay.scrollStrategies.close();
    const overlayRef = this.#overlay.create({
      positionStrategy: positionStrategy,
      scrollStrategy: scrollStrategy,
    });

    let menuItems: ContextMenuItem[] = [...this.FULL_MENU];
    if (this.user()?.role !== UserRole.Owner) {
      if (
        this.user()?.role === UserRole.User &&
        this.participant()?.kind === UserRole.User &&
        this.participant()?.userId !== this.user()?.id
      ) {
        menuItems = this.#removeEditMenuItem(menuItems);
      }
    }

    if (this.participant()?.kind !== ParticipantKind.Client) {
      menuItems = this.#removeLinkMenuItem(menuItems);
    }

    if (menuItems.length === 0) {
      menuItems.push({
        label: 'SHARED.PARTICIPANT_LIST.MENU.NO_ITEMS',
      });
    }

    const more = new ComponentPortal(ContextMenuComponent);
    const ref = overlayRef.attach(more);
    ref.setInput('items', menuItems);
    ref.instance.itemClicked.subscribe((value) => {
      if (value) {
        this.menuClicked.emit(value);
      }
      overlayRef.detach();
    });
    overlayRef._outsidePointerEvents.subscribe(() => {
      overlayRef.detach();
    });
  }

  #updateDateSignalToCheck() {
    this.#now.set(new Date());
    if (this.#dateUpdaterTimeout !== null) {
      clearTimeout(this.#dateUpdaterTimeout);
    }
    this.#dateUpdaterTimeout = window.setTimeout(() => {
      this.#updateDateSignalToCheck();
    }, 1000);
  }

  #removeEditMenuItem(menuItems: ContextMenuItem[]) {
    const filteredMenuItems = menuItems.filter(
      (item) => !(item.clickValue && item.clickValue.type === 'edit')
    );
    return filteredMenuItems;
  }

  #removeLinkMenuItem(menuItems: ContextMenuItem[]) {
    const filteredMenuItems = menuItems.filter(
      (item) => !(item.clickValue && item.clickValue.type === 'link')
    );
    return filteredMenuItems;
  }
}
